import React, { useEffect, useRef } from 'react'
import * as echarts from 'echarts'
import { Box } from '@mui/material'

export interface SentimentData {
  category: string;
  positive: number;
  neutral: number;
  negative: number;
  sentimentIndex?: number;
}

interface MultiSentimentDivergingBarProps {
  data: SentimentData[];
  title?: string;
}

export const MultiSentimentDivergingBar: React.FC<MultiSentimentDivergingBarProps> = ({
  data,
  title = 'Sentiment Analysis'
}) => {
  const chartRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!chartRef.current || !data || data.length === 0) return

    // Get the actual container dimensions
    const containerWidth = chartRef.current.offsetWidth || 800
    const containerHeight = chartRef.current.offsetHeight || Math.max(400, data.length * 60 + 150)
    
    // Initialize with explicit dimensions
    const chart = echarts.init(chartRef.current, null, {
      width: containerWidth,
      height: containerHeight
    })
    
    // Prepare data for the chart
    const categories = data.map(d => d.category)
    const negativeData = data.map(d => -(d.negative * 100))
    const neutralData = data.map(d => d.neutral * 100)
    const positiveData = data.map(d => d.positive * 100)
    const sentimentIndices = data.map(d => 
      d.sentimentIndex !== undefined ? d.sentimentIndex : (d.positive - d.negative) * 100
    )

    const option = {
      title: {
        text: title,
        left: 'center',
        top: 10,
        textStyle: {
          fontSize: 18,
          fontWeight: 'bold'
        }
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        },
        formatter: function(params: any) {
          const categoryIndex = params[0].dataIndex
          const sentiment = sentimentIndices[categoryIndex]
          
          return params[0].name + '<br/>' +
            params.map((p: any) => {
              const value = Math.abs(p.value)
              return p.marker + ' ' + p.seriesName + ': ' + value.toFixed(0) + '%'
            }).join('<br/>') +
            '<br/><strong>Sentiment Index: ' + sentiment.toFixed(0) + '</strong>'
        }
      },
      legend: {
        data: ['Negative', 'Neutral', 'Positive'],
        bottom: 10
      },
      grid: {
        top: 60,
        bottom: 60,
        left: '20%',
        right: '15%',
        containLabel: true
      },
      xAxis: {
        type: 'value',
        axisLabel: {
          formatter: function(value: number) {
            return Math.abs(value) + '%'
          }
        },
        max: 100,
        min: -50,
        splitLine: {
          show: true,
          lineStyle: {
            type: 'dashed',
            opacity: 0.3
          }
        }
      },
      yAxis: {
        type: 'category',
        axisTick: {
          show: false
        },
        data: categories,
        inverse: true,
        axisLabel: {
          fontSize: 11,
          formatter: function(value: string) {
            return value.length > 30 ? value.substring(0, 30) + '...' : value
          }
        }
      },
      series: [
        {
          name: 'Negative',
          type: 'bar',
          stack: 'total',
          label: {
            show: true,
            position: 'inside',
            formatter: function(params: any) {
              const value = Math.abs(params.value)
              return value > 5 ? value.toFixed(0) + '%' : ''
            },
            color: '#fff',
            fontSize: 11
          },
          emphasis: {
            focus: 'series'
          },
          itemStyle: {
            color: '#e74c3c'
          },
          data: negativeData
        },
        {
          name: 'Neutral',
          type: 'bar',
          stack: 'total',
          label: {
            show: true,
            position: 'inside',
            formatter: function(params: any) {
              return params.value > 5 ? params.value.toFixed(0) + '%' : ''
            },
            color: '#fff',
            fontSize: 11
          },
          emphasis: {
            focus: 'series'
          },
          itemStyle: {
            color: '#95a5a6'
          },
          data: neutralData
        },
        {
          name: 'Positive',
          type: 'bar',
          stack: 'total',
          label: {
            show: true,
            position: 'inside',
            formatter: function(params: any) {
              return params.value > 5 ? params.value.toFixed(0) + '%' : ''
            },
            color: '#fff',
            fontSize: 11
          },
          emphasis: {
            focus: 'series'
          },
          itemStyle: {
            color: '#3498db'
          },
          data: positiveData
        }
      ],
      graphic: [
        // Vertical zero line
        {
          type: 'line',
          shape: {
            x1: 0,
            y1: 0,
            x2: 0,
            y2: 1
          },
          left: 'center',
          top: 60,
          bottom: 60,
          style: {
            stroke: '#333',
            lineWidth: 2
          }
        },
        // Add sentiment index labels for each row
        ...sentimentIndices.map((index, i) => ({
          type: 'text',
          right: 50,
          top: 90 + (i * (containerHeight - 150) / data.length),
          style: {
            text: index.toFixed(0),
            fontSize: 12,
            fontWeight: 'bold',
            fill: index > 0 ? '#2ecc71' : '#e74c3c',
            textAlign: 'center'
          }
        }))
      ]
    }

    chart.setOption(option)

    const handleResize = () => {
      chart.resize()
    }

    window.addEventListener('resize', handleResize)
    
    // Add ResizeObserver for better size detection
    const resizeObserver = new ResizeObserver(() => {
      const newWidth = chartRef.current?.offsetWidth || 800
      const newHeight = chartRef.current?.offsetHeight || Math.max(400, data.length * 60 + 150)
      chart.resize({
        width: newWidth,
        height: newHeight
      })
    })
    
    if (chartRef.current) {
      resizeObserver.observe(chartRef.current)
    }
    
    // Force resize after a short delay to ensure proper rendering
    setTimeout(() => {
      const width = chartRef.current?.offsetWidth || 800
      const height = chartRef.current?.offsetHeight || Math.max(400, data.length * 60 + 150)
      chart.resize({
        width: width,
        height: height
      })
    }, 100)

    return () => {
      window.removeEventListener('resize', handleResize)
      resizeObserver.disconnect()
      chart.dispose()
    }
  }, [data, title])

  const minHeight = Math.max(500, (data?.length || 0) * 60 + 150)

  return (
    <Box
      ref={chartRef}
      sx={{
        width: '100%',
        height: `${minHeight}px`,
        minHeight: `${minHeight}px`,
        display: 'block',
        position: 'relative'
      }}
    />
  )
}